import { ReactComponent as WarningStaticIcon } from '@basqet-dashboard/common/assets/icons/warning-static.svg';
import { Button, Dropdown, FormInput, Modal } from '@basqet-dashboard/common/components';
import { COUNTRY_OPTIONS } from '@basqet-dashboard/common/config/countries';
import { FileUpload, InfoLabel } from '@components/onboarding';
import { ANALYTICS_EVENTS, KYB_ACCOUNT_TYPE } from '@config';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAnalytics, useTrackOnMount } from '@hooks';
import { useCompliance } from '@hooks/useCompliance';
import { getRemoteFileByAttachmentField, mapAttachmentMetaByField } from '@utils/attachment-meta';
import { mapCorporateBusinessDetails } from '@utils/compliance-mappers';
import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';

import { KYB_SAMPLE_DOCS } from '../../shared-constants';
import { INDUSTRY_OPTIONS, MONTHLY_VOLUME_OPTIONS } from '../corporate-non-fi-constants';
import { businessDetailsSchema } from './business-details.schema';

const FILE_FIELDS = {
  PROOF_OF_ADDRESS: 'proof_of_business_address'
};

const BusinessDetails = ({ onComplete, initialData, readOnly = false }) => {
  const { track } = useAnalytics();
  const hasTrackedStart = useRef(false);
  const attachmentMetaByField = mapAttachmentMetaByField(initialData?.attachment_meta);

  useTrackOnMount(ANALYTICS_EVENTS.KYB_BASIC_INFO_VIEWED, {
    kyb_account_type: KYB_ACCOUNT_TYPE.CORPORATE_NON_FI
  });

  const formDefaults = initialData
    ? {
        operating_business_name: initialData.operating_business_name || '',
        industry: initialData.industry || '',
        custom_industry: initialData.custom_industry || '',
        nature_of_business: initialData.nature_of_business || '',
        business_address: initialData.business_address || '',
        country: initialData.country || '',
        monthly_volume: initialData.monthly_volume || '',
        website_link: initialData.website_link || ''
      }
    : undefined;

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isValid }
  } = useForm({
    resolver: yupResolver(businessDetailsSchema),
    mode: 'onChange',
    defaultValues: formDefaults
  });

  const watchedIndustry = useWatch({ control, name: 'industry' });

  const { uploadFile, saveSection } = useCompliance();

  const [files, setFiles] = useState({
    [FILE_FIELDS.PROOF_OF_ADDRESS]: getRemoteFileByAttachmentField(
      attachmentMetaByField,
      'proof_of_address_attachment_id'
    )
  });
  const [attachmentIds, setAttachmentIds] = useState({
    [FILE_FIELDS.PROOF_OF_ADDRESS]: initialData?.proof_of_address_attachment_id || null
  });
  const [fileErrors, setFileErrors] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFormFocus = () => {
    if (!hasTrackedStart.current) {
      track(ANALYTICS_EVENTS.KYB_BASIC_INFO_STARTED, {
        kyb_account_type: KYB_ACCOUNT_TYPE.CORPORATE_NON_FI
      });
      hasTrackedStart.current = true;
    }
  };

  const handleFileChange = async (field, file) => {
    if (!file) return;

    setFiles((prev) => ({ ...prev, [field]: file }));
    setFileErrors((prev) => ({ ...prev, [field]: undefined }));

    try {
      track(ANALYTICS_EVENTS.KYB_DOC_UPLOADED, {
        kyb_account_type: KYB_ACCOUNT_TYPE.CORPORATE_NON_FI,
        doc_type: field
      });

      const result = await uploadFile(file);
      setAttachmentIds((prev) => ({ ...prev, [field]: result.attachment_id }));

      if (field === FILE_FIELDS.PROOF_OF_ADDRESS) {
        toast.success('Proof of Business Operating Address uploaded successfully');
      }
    } catch (error) {
      setFiles((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleDeleteRequest = (field) => {
    setDeleteTarget(field);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    track(ANALYTICS_EVENTS.KYB_DOC_DELETED, {
      kyb_account_type: KYB_ACCOUNT_TYPE.CORPORATE_NON_FI,
      doc_type: deleteTarget
    });
    setFiles((prev) => ({ ...prev, [deleteTarget]: null }));
    setAttachmentIds((prev) => ({ ...prev, [deleteTarget]: null }));
    setDeleteTarget(null);
    setShowDeleteModal(false);
  };

  const validateFiles = () => {
    const errs = {};
    if (!attachmentIds[FILE_FIELDS.PROOF_OF_ADDRESS])
      errs[FILE_FIELDS.PROOF_OF_ADDRESS] = 'Proof of business operating address is required';
    setFileErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const onSubmit = async (data) => {
    if (!validateFiles()) return;

    try {
      setIsSubmitting(true);

      track(ANALYTICS_EVENTS.KYB_BASIC_INFO_SAVED, {
        kyb_account_type: KYB_ACCOUNT_TYPE.CORPORATE_NON_FI
      });

      const apiData = mapCorporateBusinessDetails(data, attachmentIds);
      const result = await saveSection('BUSINESS_DETAILS', apiData, {
        entryId: initialData?._entryId,
        version: initialData?._version
      });

      toast.success('Business Details saved successfully');
      onComplete({
        ...data,
        ...attachmentIds,
        _entryId: result.entry_id,
        _version: result.version
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to save business details:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="corp-step-form" onSubmit={handleSubmit(onSubmit)} onFocus={handleFormFocus}>
      <fieldset disabled={readOnly} style={{ border: 'none', padding: 0, margin: 0 }}>
        <div className="corp-step-form__row">
          <div className="corp-step-form__field">
            <FormInput
              label={
                <InfoLabel
                  text="Operating Business Name"
                  tooltip="Enter the official operating name of your company as registered with the relevant authorities."
                />
              }
              name="operating_business_name"
              placeholder="Enter business name"
              register={register('operating_business_name')}
              error={errors.operating_business_name?.message}
            />
          </div>

          <div className="corp-step-form__field">
            <Dropdown
              name="industry"
              formLabel="Select Industry"
              placeholder="Select Industry"
              options={INDUSTRY_OPTIONS}
              register={register('industry')}
              defaultValue={initialData?.industry}
              error={errors.industry?.message}
            />
          </div>
        </div>

        {watchedIndustry === 'other' && (
          <div className="corp-step-form__row">
            <div className="corp-step-form__field">
              <FormInput
                label="Enter Industry"
                name="custom_industry"
                placeholder="Enter your industry"
                register={register('custom_industry')}
                error={errors.custom_industry?.message}
              />
            </div>
            <div className="corp-step-form__field">
              <FormInput
                label="Nature of Business"
                name="nature_of_business"
                placeholder="e.g Online retail of electronics"
                register={register('nature_of_business')}
                error={errors.nature_of_business?.message}
              />
            </div>
          </div>
        )}

        {watchedIndustry !== 'other' && (
          <div className="corp-step-form__field">
            <FormInput
              label="Nature of Business"
              name="nature_of_business"
              placeholder="e.g Online retail of electronics"
              register={register('nature_of_business')}
              error={errors.nature_of_business?.message}
            />
          </div>
        )}

        <div className="corp-step-form__row">
          <div className="corp-step-form__field">
            <FormInput
              label="Business Address"
              name="business_address"
              placeholder="Enter business address"
              register={register('business_address')}
              error={errors.business_address?.message}
            />
          </div>

          <div className="corp-step-form__field">
            <Dropdown
              name="country"
              formLabel="Which country are you in?"
              placeholder="Select Country"
              options={COUNTRY_OPTIONS}
              register={register('country')}
              defaultValue={initialData?.country}
              error={errors.country?.message}
            />
          </div>
        </div>

        <div className="corp-step-form__section">
          <h3 className="corp-step-form__section-title">Proof of Business Operating Address</h3>
          <p className="corp-step-form__section-subtitle">
            Please note that the proof of address required is your utility bill or bank statement no
            older than 3 months
          </p>
          <div className="corp-step-form__field">
            <FileUpload
              value={files[FILE_FIELDS.PROOF_OF_ADDRESS]}
              onChange={(file) => handleFileChange(FILE_FIELDS.PROOF_OF_ADDRESS, file)}
              onDelete={() => handleDeleteRequest(FILE_FIELDS.PROOF_OF_ADDRESS)}
              onView={() =>
                track(ANALYTICS_EVENTS.KYB_DOC_VIEWED, {
                  kyb_account_type: KYB_ACCOUNT_TYPE.CORPORATE_NON_FI,
                  doc_type: FILE_FIELDS.PROOF_OF_ADDRESS
                })
              }
              sampleTitle={{ normal: 'Sample', cursive: 'Proof of Address' }}
              sampleImage={KYB_SAMPLE_DOCS.PROOF_OF_ADDRESS}
              maxSizeMB={5}
              error={fileErrors[FILE_FIELDS.PROOF_OF_ADDRESS]}
              disabled={readOnly}
            />
          </div>
        </div>

        <div className="corp-step-form__row">
          <div className="corp-step-form__field">
            <Dropdown
              name="monthly_volume"
              formLabel="Monthly Transaction Volume"
              placeholder="Expected Monthly Volume"
              options={MONTHLY_VOLUME_OPTIONS}
              register={register('monthly_volume')}
              defaultValue={initialData?.monthly_volume}
              error={errors.monthly_volume?.message}
            />
          </div>

          <div className="corp-step-form__field">
            <FormInput
              label="Website Link"
              name="website_link"
              type="text"
              placeholder="https://businesswebsite.com"
              register={register('website_link')}
              error={errors.website_link?.message}
            />
          </div>
        </div>
      </fieldset>
      {!readOnly && (
        <Button
          type="submit"
          isFull
          isLoading={isSubmitting}
          disabled={!isValid || !attachmentIds[FILE_FIELDS.PROOF_OF_ADDRESS]}>
          Save and Continue
        </Button>
      )}

      {showDeleteModal && (
        <Modal
          Icon={WarningStaticIcon}
          header={{ name: 'Delete', cursive: 'Document' }}
          onModalClose={() => setShowDeleteModal(false)}>
          <p className="modal__text">Are you sure you want to delete this document?</p>
          <Button isFull variant="outline" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button isFull onClick={confirmDelete}>
            Yes, continue
          </Button>
        </Modal>
      )}
    </form>
  );
};

BusinessDetails.propTypes = {
  onComplete: PropTypes.func.isRequired,
  initialData: PropTypes.object,
  readOnly: PropTypes.bool
};

export { BusinessDetails };
